/** `TagCategoryAssociationsTable` — every flag of every tag category at once, compact, with
 *  the rules visible (backend plan 2026-10-07 §A1). A controlled component: the operator's
 *  Defaults section drives it from `associationsReducer`, the tour editor (later) from its
 *  draft — no second table.
 *
 *  - Columns: Category (sticky left) · Tags (`min–max`, a popover editor) · one ~34 px
 *    column per flag, grouped under a band with vertical labels and a count of rows that
 *    have the flag on (Check-in `n/3`, Boarding `n/1`, amber + icon past the limit) · row
 *    actions (sticky right). The flags scroll sideways between the sticky columns.
 *  - View mode shows each value as a shape + words (`BoolText`; Check-in as its place in
 *    the check-in order); edit mode swaps in dense checkboxes in the same cells. Nothing
 *    moves on Edit (Zero-Re-Layout): the actions slots, the add slot and the warning line
 *    keep their room in both modes.
 *  - A flag's dependants change with it in one action; a flag that cannot change says why
 *    (a rule or a limit) in its tooltip. Changed cells are tinted amber against
 *    `baseline`, with a row revert; removed rows stay in place, struck through, until
 *    save. Inherited rows are muted, say where they come from, and offer Override.
 *  - Hovering or focusing a cell highlights its row and column.
 *  - An edit re-renders only the rows it changes: the row models keep their identity while
 *    unchanged (`keepUnchanged`), the rows and the flag headers are memoized, and the action
 *    handler the rows get is stable. The crosshair is CSS (`:hover`, `data-hcol`) — moving
 *    the pointer renders nothing; moving focus re-renders the two rows it leaves and enters.
 *  - Keyboard: the matrix is ONE tab stop; arrows move, Space toggles, Enter opens the
 *    Tags editor, Alt+↑/↓ moves the row, Delete removes it.
 *
 *  Accessible names follow the tour table ("Check-in of MEAL", "Override ROOM", "Move BUS
 *  up", "Remove MEAL", "Restore MEAL", "Tags per guest of MEAL") and the rows carry
 *  `data-tagcat-row` / `data-tagcat-default` / `data-removed` / `data-overridden`, so the
 *  tour editor can adopt this table without rewriting its tests. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  memo,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';

import { visuallyHidden } from '@/components/BoolText';
import type { ValueAction } from '@/components/ValueActions/ValueActions';

import { effectiveRows, limitState, withOverrides } from './associationsReducer';
import { AssociationRow } from './AssociationRow';
import { ALL_FLAGS, FLAG_BY_KEY, FLAG_GROUPS, KEY_FLAGS, type Flag } from './flags';
import { makeRank, navigate, resolve, type NavPos, type NavRow } from './gridNav';
import {
  ACTIONS_PX,
  BAND_PX,
  CATEGORY_PX,
  CELL_FOCUS,
  COUNT_PX,
  FLAG_PX,
  HIGHLIGHT,
  LABEL_PX,
  TAGS_PX,
  tableWidth,
} from './layout';
import { buildRowModels, keepUnchanged, type RowModel } from './rowModel';
import { MAX_BOARDING, MAX_CHECKIN } from './rules';
import type { AssocAction, AssocCategory, AssocRow, InheritedRow } from './types';

export type AssocColumns = 'all' | 'key' | readonly Flag[];

export interface TagCategoryAssociationsTableProps {
  /** Own rows, in display order (= sequence). */
  rows: readonly AssocRow[];
  /** Read-only rows from the scope above (a franchise parent's defaults, the operator's
   *  defaults on a tour), shown after the own rows. */
  inherited?: readonly InheritedRow[];
  /** The stored own rows by key: changed cells are tinted against it and a row can be
   *  reverted to it. */
  baseline?: ReadonlyMap<string, AssocRow>;
  /** Edit mode (controls) vs view mode (values). */
  editable: boolean;
  /** Why the table cannot be edited — a line with a lock icon above it. */
  lockReason?: string | null;
  /** The flag columns: all 19, the six key ones, or a list (shown in catalog order). */
  columns?: AssocColumns;
  onAction?: (a: AssocAction) => void;
  /** The host's category picker (its scope differs per host), at the toolbar's end. */
  addPicker?: ReactNode;
  /** The category name's quick actions (ValueActions). */
  categoryActions?: (c: AssocCategory) => ValueAction[];
  /** Wraps (or replaces) the content of each own row's flag and Tags cell — the tour
   *  editor's seam for its `DraftCell`. */
  renderCell?: (row: AssocRow, field: Flag | 'counts', cell: ReactNode) => ReactNode;
  /** A line under an own or inherited row (row issues, server errors). */
  renderRowAddon?: (row: AssocRow) => ReactNode;
  /** The inherited rows' chip ("from Cascadia"). */
  ownerLabel?: (r: InheritedRow) => string;
  /** The grid's accessible name. */
  label?: string;
  /** Shown when there are no rows. */
  emptyText?: string;
  /** The scroll box's height cap; the header sticks inside it. */
  maxHeight?: number | string;
}

const NO_INHERITED: readonly InheritedRow[] = [];
const NOOP = () => {};
const HEAD = 'head';

export const defaultOwnerLabel = (r: InheritedRow) =>
  `from ${r.owner.name || r.owner.code || `#${r.owner.id}`}`;

/** The visible flag columns, in catalog order. */
export function visibleFlags(columns: AssocColumns): Flag[] {
  if (columns === 'all') return [...ALL_FLAGS];
  const set = new Set<Flag>(columns === 'key' ? KEY_FLAGS : columns);
  return ALL_FLAGS.filter((f) => set.has(f));
}

const parseNav = (v: string | null): NavPos | null => {
  if (!v) return null;
  const i = v.lastIndexOf('|');
  return i < 0 ? null : { row: v.slice(0, i), col: v.slice(i + 1) };
};

const MOVE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

export function TagCategoryAssociationsTable({
  rows,
  inherited = NO_INHERITED,
  baseline,
  editable,
  lockReason,
  columns = 'all',
  onAction = NOOP,
  addPicker,
  categoryActions,
  renderCell,
  renderRowAddon,
  ownerLabel = defaultOwnerLabel,
  label = 'Tag categories',
  emptyText = 'No tag categories.',
  maxHeight = '70vh',
}: TagCategoryAssociationsTableProps) {
  const columnsKey = typeof columns === 'string' ? columns : columns.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the list's content
  const flags = useMemo(() => visibleFlags(columns), [columnsKey]);
  const groups = useMemo(
    () =>
      FLAG_GROUPS.map((g) => ({ ...g, flags: g.flags.filter((f) => flags.includes(f)) })).filter(
        (g) => g.flags.length > 0,
      ),
    [flags],
  );
  const groupStarts = useMemo(() => new Set(groups.slice(1).map((g) => g.flags[0]!)), [groups]);
  const rank = useMemo(() => makeRank(flags), [flags]);

  const inh = useMemo(() => withOverrides(rows, inherited), [rows, inherited]);
  // The last committed models: an unchanged row keeps its model object, so its memoized
  // row skips the render.
  const prevModels = useRef<readonly RowModel[]>([]);
  const models = useMemo(
    () =>
      keepUnchanged(
        prevModels.current,
        buildRowModels({ rows, inherited: inh, baseline, editable, flags }),
      ),
    [rows, inh, baseline, editable, flags],
  );
  useLayoutEffect(() => {
    prevModels.current = models;
  }, [models]);
  const effective = useMemo(() => effectiveRows(rows, inherited), [rows, inherited]);
  const limits = useMemo(() => limitState(rows, inherited, baseline), [rows, inherited, baseline]);
  // A breach in view mode is a fact (amber); save advice — and the error for a breach the
  // draft made worse — only while editing.
  const limitInvalid = editable && limits.worse;
  const saveAdvice = limits.worse
    ? ' — clear one to save'
    : ' — stored like this; saving other changes is fine';

  const navRows: NavRow[] = useMemo(
    () => [
      { id: HEAD, stops: ['category', 'counts', ...flags] },
      ...models.map((m) => ({ id: m.navId, stops: m.stops })),
    ],
    [flags, models],
  );
  const [active, setActive] = useState<NavPos | null>(null);
  const current = resolve(navRows, active, rank, models[0]?.navId ?? HEAD);
  const tableRef = useRef<HTMLTableElement>(null);
  const pending = useRef<NavPos | null>(null);
  const keysId = useId();

  const focusPos = useCallback((pos: NavPos) => {
    const want = `${pos.row}|${pos.col}`;
    const el = [...(tableRef.current?.querySelectorAll<HTMLElement>('[data-nav]') ?? [])].find(
      (e) => e.getAttribute('data-nav') === want,
    );
    el?.focus();
  }, []);

  // After an action, focus follows its row once it renders (see `handleAction`).
  useLayoutEffect(() => {
    if (!pending.current) return;
    const target = resolve(navRows, pending.current, rank, models[0]?.navId ?? HEAD);
    pending.current = null;
    if (target) {
      // Usually where focus already is: no second render for the same place.
      setActive((p) => (p && p.row === target.row && p.col === target.col ? p : target));
      focusPos(target);
    }
  }, [navRows, rank, models, focusPos]);

  const modelByNav = useMemo(() => new Map(models.map((m) => [m.navId, m])), [models]);
  // What the stable action handler reads (it is called from events, after the commit).
  const latest = useRef({ onAction, modelByNav, navRows, rank });
  useLayoutEffect(() => {
    latest.current = { onAction, modelByNav, navRows, rank };
  });

  /** Every action keeps the keyboard where it was. A button that goes away with its action
   *  (Remove → Restore, Revert, Override) or a row that moves would otherwise drop focus to
   *  the page: focus goes to the same cell of the row (its nearest stop) — or, when a new
   *  row leaves the table, to the row below (else above). Stable (it reads `latest`), so
   *  the memoized rows are not re-rendered by a new handler — whatever the host passes. */
  const handleAction = useCallback((a: AssocAction) => {
    const { onAction, modelByNav, navRows, rank } = latest.current;
    const el = document.activeElement as HTMLElement | null;
    const pos =
      el && tableRef.current?.contains(el)
        ? parseNav(el.closest<HTMLElement>('[data-nav]')?.getAttribute('data-nav') ?? null)
        : null;
    if (pos) {
      const leaves = a.type === 'remove' && modelByNav.get(pos.row)?.row.state === 'new';
      pending.current = leaves
        ? (navigate(navRows, pos, 'ArrowDown', { rank }) ??
          navigate(navRows, pos, 'ArrowUp', { rank }))
        : pos;
    }
    onAction(a);
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    const target = e.target as HTMLElement;
    // Keys typed in a popover (Tags editor, value actions) reach here through the React
    // tree; they are not the grid's.
    if (!tableRef.current?.contains(target)) return;
    const stop = target.closest<HTMLElement>('[data-nav]');
    const pos = parseNav(stop?.getAttribute('data-nav') ?? null);
    if (!pos) return;
    const model = modelByNav.get(pos.row);

    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const delta = e.key === 'ArrowUp' ? -1 : 1;
      if (model?.live && (delta < 0 ? model.canUp : model.canDown)) {
        handleAction({ type: 'move', key: model.row.key, delta });
      }
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && model?.live) {
      e.preventDefault();
      handleAction({ type: 'remove', key: model.row.key });
      return;
    }
    if (e.altKey || e.metaKey) return;
    if (MOVE_KEYS.has(e.key)) {
      e.preventDefault();
      const next = navigate(navRows, pos, e.key, { ctrl: e.ctrlKey, rank });
      if (next) {
        setActive(next);
        focusPos(next);
      }
      return;
    }
    // Space on a cell that is its own stop (a locked flag, a read-only value) would scroll.
    if (e.key === ' ' && stop === target && (target.tagName === 'TD' || target.tagName === 'TH')) {
      e.preventDefault();
    }
  };

  const setHighlight = (el: HTMLElement | null) => {
    const table = tableRef.current;
    if (!table) return;
    const col = el?.closest<HTMLElement>('[data-col]')?.getAttribute('data-col') ?? '';
    if (table.getAttribute('data-hcol') !== col) table.setAttribute('data-hcol', col);
  };
  const onFocus = (e: FocusEvent<HTMLTableElement>) => {
    const target = e.target as HTMLElement;
    if (!tableRef.current?.contains(target)) return;
    const pos = parseNav(
      target.closest<HTMLElement>('[data-nav]')?.getAttribute('data-nav') ?? null,
    );
    if (pos && (pos.row !== current?.row || pos.col !== current?.col)) setActive(pos);
    setHighlight(target);
  };
  const onBlur = (e: FocusEvent<HTMLTableElement>) => {
    if (!tableRef.current?.contains(e.relatedTarget as Node | null)) setHighlight(null);
  };

  // The crosshair's column half: one static rule per column, switched by `data-hcol`.
  const crosshairSx = useMemo(() => {
    const out: Record<string, object> = {};
    for (const c of ['category', 'counts', ...flags]) {
      out[`&[data-hcol="${c}"] [data-col="${c}"]`] = { boxShadow: HIGHLIGHT };
    }
    return out;
  }, [flags]);

  const ownLive = rows.filter((r) => r.state !== 'removed').length;
  const inheritedLive = inh.filter((r) => !r.overriddenBy).length;
  const colCount = 3 + flags.length;
  const counts = useMemo(() => {
    const out = {} as Record<Flag, number>;
    for (const f of flags) out[f] = effective.filter((r) => r.flags[f]).length;
    return out;
  }, [effective, flags]);
  const activeIn = (navId: string) => (current?.row === navId ? current.col : null);
  const headNav = (col: string) => ({
    tabIndex: activeIn(HEAD) === col ? 0 : -1,
    'data-nav': `${HEAD}|${col}`,
  });

  // The group band, when every visible group has room for its title (a narrow host's
  // one-column groups would only show "Gu…"; the header tooltips name the group anyway).
  const showBand = groups.every((g) => g.flags.length >= 2);
  const headRows = showBand ? 2 : 1;
  const headLeft = (
    <>
      <TableCell
        rowSpan={headRows}
        data-col="category"
        {...headNav('category')}
        sx={{
          ...HEAD_SX,
          ...STICKY_HEAD_SX,
          left: 0,
          zIndex: 4,
          borderRight: 1,
          borderRightColor: 'divider',
          px: 1,
          ...CELL_FOCUS,
        }}
      >
        Category
      </TableCell>
      <Tooltip title={TAGS_TIP} describeChild placement="top">
        <TableCell
          rowSpan={headRows}
          data-col="counts"
          {...headNav('counts')}
          sx={{ ...HEAD_SX, ...STICKY_HEAD_SX, textAlign: 'center', ...CELL_FOCUS }}
        >
          Tags
        </TableCell>
      </Tooltip>
    </>
  );
  const headRight = (
    <TableCell
      rowSpan={headRows}
      sx={{
        ...HEAD_SX,
        ...STICKY_HEAD_SX,
        right: 0,
        zIndex: 4,
        borderLeft: 1,
        borderLeftColor: 'divider',
      }}
    >
      <Box component="span" sx={visuallyHidden}>
        Row actions
      </Box>
    </TableCell>
  );
  const flagHeads = flags.map((f) => (
    <FlagHeader
      key={f}
      flag={f}
      count={counts[f]}
      total={effective.length}
      groupStart={groupStarts.has(f)}
      top={showBand ? BAND_PX : 0}
      active={activeIn(HEAD) === f}
      groupTitle={groups.find((g) => g.flags.includes(f))?.title ?? ''}
    />
  ));

  return (
    // As wide as the table (the toolbar and the line under it align with its edges), never
    // wider than the host.
    <Box
      sx={{ display: 'grid', gap: 0.75, minWidth: 0, width: 'fit-content', maxWidth: '100%' }}
      data-assoc-table
    >
      {/* The toolbar keeps the picker's room in both modes (Zero-Re-Layout). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 40, minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          data-assoc-summary
        >
          {ownLive === 0 && inh.length === 0
            ? 'No categories'
            : `${ownLive} ${ownLive === 1 ? 'category' : 'categories'}`}
          {inh.length > 0 &&
            ` · ${inheritedLive} inherited${inh.length > inheritedLive ? ` (${inh.length - inheritedLive} overridden)` : ''}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {addPicker && <Box sx={{ flex: 'none', width: 260, maxWidth: '100%' }}>{addPicker}</Box>}
      </Box>
      {lockReason && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
          data-assoc-locked
        >
          <LockOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
          <span>{lockReason}</span>
        </Typography>
      )}
      <Box
        sx={{
          overflow: 'auto',
          maxHeight,
          maxWidth: '100%',
          width: 'fit-content',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Table
          ref={tableRef}
          size="small"
          role="grid"
          aria-label={label}
          aria-readonly={!editable || undefined}
          aria-describedby={keysId}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          onMouseOver={(e: MouseEvent<HTMLTableElement>) => setHighlight(e.target as HTMLElement)}
          onMouseLeave={() => setHighlight(null)}
          sx={{
            tableLayout: 'fixed',
            width: tableWidth(flags.length),
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& td, & th': { borderBottom: 1, borderBottomColor: 'divider' },
            '& tbody tr:last-of-type > td, & tbody tr:last-of-type > th': { borderBottom: 0 },
            '& tbody tr[data-row]:hover > *, & tbody tr[data-row]:focus-within > *': {
              boxShadow: HIGHLIGHT,
            },
            '& tbody tr[data-first-inherited] > *': { borderTop: 2, borderTopColor: 'divider' },
            ...crosshairSx,
          }}
        >
          <colgroup>
            <col style={{ width: CATEGORY_PX }} />
            <col style={{ width: TAGS_PX }} />
            {flags.map((f) => (
              <col key={f} style={{ width: FLAG_PX }} />
            ))}
            <col style={{ width: ACTIONS_PX }} />
          </colgroup>
          <TableHead>
            {showBand ? (
              <>
                <TableRow sx={{ height: BAND_PX }}>
                  {headLeft}
                  {groups.map((g, i) => (
                    <TableCell
                      key={g.id}
                      colSpan={g.flags.length}
                      scope="colgroup"
                      sx={{
                        ...HEAD_SX,
                        ...STICKY_HEAD_SX,
                        height: BAND_PX,
                        boxSizing: 'border-box',
                        fontSize: 11,
                        lineHeight: 1.15,
                        textAlign: 'center',
                        px: 0.25,
                        py: 0.25,
                        ...(i > 0 ? { borderLeft: 1, borderLeftColor: 'divider' } : {}),
                      }}
                    >
                      {g.title}
                    </TableCell>
                  ))}
                  {headRight}
                </TableRow>
                <TableRow>{flagHeads}</TableRow>
              </>
            ) : (
              <TableRow>
                {headLeft}
                {flagHeads}
                {headRight}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {models.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ py: 1.5, px: 1.5, color: 'text.secondary' }}>
                  <Box sx={{ position: 'sticky', left: 12, width: 'fit-content' }}>{emptyText}</Box>
                </TableCell>
              </TableRow>
            )}
            {models.map((m) => (
              <AssociationRow
                key={m.navId}
                model={m}
                flags={flags}
                groupStarts={groupStarts}
                editable={editable}
                activeCol={activeIn(m.navId)}
                colCount={colCount}
                ownerText={m.inherited ? ownerLabel(m.inherited) : undefined}
                categoryActions={categoryActions}
                renderCell={renderCell}
                renderRowAddon={renderRowAddon}
                onAction={handleAction}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
      {/* One line, reserved in both modes: the limits on the left, the keys on the right. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          columnGap: 2,
          rowGap: 0.25,
          minHeight: 20,
        }}
      >
        <Box aria-live="polite" sx={{ flex: '1 1 320px', minWidth: 0 }} data-assoc-limits>
          {limits.problems.length > 0 && (
            <Typography
              variant="caption"
              color={limitInvalid ? 'error' : 'warning.dark'}
              sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
              data-row-issue={limitInvalid ? 'invalid' : 'warning'}
            >
              {limitInvalid ? (
                <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
              ) : (
                <WarningAmberIcon aria-hidden sx={{ fontSize: 16 }} />
              )}
              {/* As the keys line: both wordings in one grid cell, the other one hidden, so
                  Edit adding the advice never re-wraps the line (Zero-Re-Layout). */}
              <Box component="span" sx={{ display: 'grid', '& > *': { gridArea: '1 / 1' } }}>
                <span style={{ visibility: editable ? 'visible' : 'hidden' }}>
                  {limits.problems.join(' · ')}
                  {saveAdvice}
                </span>
                <span style={{ visibility: editable ? 'hidden' : 'visible' }}>
                  {limits.problems.join(' · ')}
                </span>
              </Box>
            </Typography>
          )}
        </Box>
        {/* Both wordings share one grid cell, the other one hidden, so the line is as wide
            as the edit-mode wording in both modes: in a narrow host that wording wraps the
            line, and Edit must not be what wraps it (Zero-Re-Layout). Hidden text is left out
            of the grid's description. */}
        <Typography
          id={keysId}
          variant="caption"
          color="text.secondary"
          sx={{
            flex: 'none',
            display: 'grid',
            justifyItems: 'end',
            '& > *': { gridArea: '1 / 1' },
          }}
          data-assoc-keys
        >
          <span style={{ visibility: editable ? 'visible' : 'hidden' }}>{EDIT_KEYS}</span>
          <span style={{ visibility: editable ? 'hidden' : 'visible' }}>{VIEW_KEYS}</span>
        </Typography>
      </Box>
    </Box>
  );
}

const EDIT_KEYS =
  'Arrow keys move · Space toggles · Enter edits tag counts · Alt+↑/↓ moves a row · Delete removes it';
const VIEW_KEYS = 'Arrow keys move between cells';

const HEAD_SX = {
  py: 0.5,
  px: 0.5,
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
  verticalAlign: 'bottom',
  bgcolor: 'background.paper',
  lineHeight: 1.2,
} as const;
const STICKY_HEAD_SX = { position: 'sticky', top: 0, zIndex: 2 } as const;

const TAGS_TIP = (
  <Box sx={{ display: 'grid', gap: 0.5 }}>
    <b>Tags per guest</b>
    <span>
      Minimum–maximum tags of this category a guest has: 0 minimum = optional, ∞ = no maximum.
    </span>
    <span>0–1 optional, one · 1–1 required, one · 0–∞ optional, many · 1–∞ required, many</span>
  </Box>
);

/** Memoized on plain values: an edit re-renders only the headers whose count changed. */
const FlagHeader = memo(function FlagHeader({
  flag,
  count,
  total,
  groupStart,
  top,
  active,
  groupTitle,
}: {
  flag: Flag;
  count: number;
  total: number;
  groupStart: boolean;
  /** Sticky offset: under the group band, or at the top without one. */
  top: number;
  /** The grid's one tab stop is this header. */
  active: boolean;
  groupTitle: string;
}) {
  const def = FLAG_BY_KEY[flag];
  const limit = flag === 'checkin' ? MAX_CHECKIN : flag === 'boarding' ? MAX_BOARDING : null;
  const over = limit !== null && count > limit;
  const countId = useId();
  const countWords =
    limit !== null
      ? `${count} of at most ${limit}${over ? ' — over the limit' : ''}`
      : `${count} of ${total} ${total === 1 ? 'category' : 'categories'}`;
  const tip = (
    <Box sx={{ display: 'grid', gap: 0.5, maxWidth: 280 }}>
      <span>
        <b>{def.label}</b> · {groupTitle}
      </span>
      <span>{def.help}</span>
      {def.rule && <span>Rule: {def.rule}</span>}
      <span>On: {countWords}</span>
    </Box>
  );
  return (
    <Tooltip title={tip} describeChild placement="top">
      <TableCell
        data-col={flag}
        aria-label={def.label}
        aria-describedby={countId}
        tabIndex={active ? 0 : -1}
        data-nav={`${HEAD}|${flag}`}
        sx={{
          ...HEAD_SX,
          position: 'sticky',
          top,
          zIndex: 2,
          p: 0,
          pt: 0.5,
          textAlign: 'center',
          ...(groupStart ? { borderLeft: 1, borderLeftColor: 'divider' } : {}),
          ...CELL_FOCUS,
        }}
      >
        <Box
          sx={{
            height: LABEL_PX,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              maxHeight: LABEL_PX,
              whiteSpace: 'normal',
              lineHeight: '14px',
              textAlign: 'left',
              overflow: 'hidden',
            }}
          >
            {def.short}
          </Box>
        </Box>
        <Box
          id={countId}
          sx={{
            height: COUNT_PX,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1px',
            fontSize: 11,
            fontWeight: over ? 700 : 500,
            fontVariantNumeric: 'tabular-nums',
            color: over ? 'warning.dark' : count > 0 ? 'text.primary' : 'text.disabled',
          }}
          data-flag-count={flag}
        >
          {over && <WarningAmberIcon aria-hidden sx={{ fontSize: 11 }} />}
          <span aria-hidden>{limit !== null ? `${count}/${limit}` : count}</span>
          <Box component="span" sx={visuallyHidden}>
            {countWords}
          </Box>
        </Box>
      </TableCell>
    </Tooltip>
  );
});
